import { useQuery } from '@tanstack/react-query';
import { FC, useEffect, useMemo } from 'react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { _getMemberActivity } from '@/api/gamma-team/queries';
import type {
  GetActivityParams,
  TeamActivityDay,
  TeamActivityMember,
  TeamGoal,
} from '@/api/gamma-team/types';
import ChevronLeft from '@/assets/svg/ui-elements/chevron-left.svg';
import ChevronRight from '@/assets/svg/ui-elements/chevron-right.svg';
import { Badge, Drawer, IconButton, theme, Typography } from '@/design-system';
import { typographyVariantStyles } from '@/design-system/typography-variants';

import { CHART_AXIS_TEXT, SINGLE_SERIES_COLOR } from './chart-palette';
import { MemberActivity } from './member-activity';
import { MemberGoals } from './member-goals';
import { ChartArea, ChartTooltipBox, DrawerBody, Section, SectionActions } from './styled';
import { memberName } from './team-sorting';
import { formatDayLabel, idleState } from './team-trends';

const DRAWER_WIDTH = 720;
const MINI_CHART_HEIGHT = 120;

const isTypingTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

export const roleLabel = (role: string | null): string | null =>
  role ? role.replace(/^Gamma/, '').replace(/([a-z])([A-Z])/g, '$1 $2') : null;

interface MiniTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: { date: string; value: number } }>;
}

const MiniTooltip: FC<MiniTooltipProps> = ({ active, payload }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <ChartTooltipBox>
      <Typography variant="body4" color={theme.colors.text.secondary}>
        {formatDayLabel(point.date)}
      </Typography>
      <Typography variant="body3" fontWeight={600}>
        {`${point.value} ${point.value === 1 ? 'activity' : 'activities'}`}
      </Typography>
    </ChartTooltipBox>
  );
};

interface MemberDrawerProps {
  member: TeamActivityMember | null;
  order: readonly string[];
  daily: TeamActivityDay[] | undefined;
  params: GetActivityParams;
  goals: TeamGoal[] | undefined;
  goalsFailed: boolean;
  onRetryGoals: () => void;
  canEditGoals: boolean;
  onChangeMember: (_userId: string) => void;
  onClose: () => void;
}

export const MemberDrawer: FC<MemberDrawerProps> = ({
  member,
  order,
  daily,
  params,
  goals,
  goalsFailed,
  onRetryGoals,
  canEditGoals,
  onChangeMember,
  onClose,
}) => {
  const userId = member?.userId ?? null;
  const memberQuery = useQuery({
    ..._getMemberActivity(userId ?? '', params),
    enabled: userId !== null,
  });

  const index = userId ? order.indexOf(userId) : -1;
  const previousId = index > 0 ? order[index - 1] : null;
  const nextId = index >= 0 && index < order.length - 1 ? order[index + 1] : null;

  useEffect(() => {
    if (!userId) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || isTypingTarget(event.target)) return;
      if ((event.key === 'ArrowDown' || event.key === 'j') && nextId) {
        event.preventDefault();
        onChangeMember(nextId);
      } else if ((event.key === 'ArrowUp' || event.key === 'k') && previousId) {
        event.preventDefault();
        onChangeMember(previousId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [userId, nextId, previousId, onChangeMember]);

  const series = useMemo(
    () =>
      daily && member?.dailyActivity
        ? daily.map((day, at) => ({ date: day.date, value: member.dailyActivity?.[at] ?? 0 }))
        : null,
    [daily, member],
  );

  const now = useMemo(() => new Date(), []);
  const idle = member ? idleState(member, now) : null;
  const name = member ? memberName(member) : '';
  const description = member
    ? [
        member.name?.trim() ? member.email : null,
        roleLabel(member.role),
        member.former ? 'Former' : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  return (
    <Drawer
      open={member !== null}
      onOpenChange={(open) => !open && onClose()}
      title={name}
      description={description || undefined}
      maxWidth={DRAWER_WIDTH}
      headerActions={
        <SectionActions>
          <Typography variant="body4" color={theme.colors.text.tertiary}>
            {index >= 0 ? `${index + 1} of ${order.length}` : ''}
          </Typography>
          <IconButton
            size="sm"
            variant="ghost"
            color="tertiary"
            aria-label="Previous member (↑)"
            disabled={!previousId}
            onClick={() => previousId && onChangeMember(previousId)}
          >
            <ChevronLeft width={20} height={20} />
          </IconButton>
          <IconButton
            size="sm"
            variant="ghost"
            color="tertiary"
            aria-label="Next member (↓)"
            disabled={!nextId}
            onClick={() => nextId && onChangeMember(nextId)}
          >
            <ChevronRight width={20} height={20} />
          </IconButton>
        </SectionActions>
      }
    >
      {member && (
        <DrawerBody>
          {idle?.kind === 'idle' && (
            <Badge
              label={`No activity for ${idle.days} days`}
              color="yellow"
              variant="subtle"
              size="sm"
            />
          )}
          {idle?.kind === 'never' && (
            <Badge label="No activity yet" color="yellow" variant="subtle" size="sm" />
          )}

          {series && series.length > 1 && (
            <Section aria-label="Daily activity">
              <Typography variant="body1" fontWeight={600}>
                Daily activity
              </Typography>
              <ChartArea h={MINI_CHART_HEIGHT}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDayLabel}
                      tick={{
                        fill: CHART_AXIS_TEXT,
                        fontSize: typographyVariantStyles.body4.fontSize,
                      }}
                      axisLine={false}
                      tickLine={false}
                      minTickGap={24}
                    />
                    <YAxis hide domain={[0, 'dataMax']} />
                    <Tooltip
                      cursor={{ fill: theme.colors.background.overlay, opacity: 0.4 }}
                      content={<MiniTooltip />}
                    />
                    <Bar
                      dataKey="value"
                      fill={SINGLE_SERIES_COLOR}
                      maxBarSize={16}
                      radius={[theme.radius[4], theme.radius[4], 0, 0]}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartArea>
            </Section>
          )}

          <MemberGoals
            userId={member.userId}
            name={name}
            goals={goals}
            failed={goalsFailed}
            onRetry={onRetryGoals}
            counts={member}
            reportedDays={daily?.length ?? 0}
            canEdit={canEditGoals && !member.former}
          />

          <MemberActivity
            key={member.userId}
            data={memberQuery.data}
            isPending={memberQuery.isPending}
            isError={memberQuery.isError}
            isFetching={memberQuery.isFetching}
            onRetry={() => memberQuery.refetch()}
          />
        </DrawerBody>
      )}
    </Drawer>
  );
};
